class HeaderMenu extends HTMLElement {
    constructor() {
        super();

        this._onMenuToggleClick = this._onMenuToggleClick.bind(this);
        this._onTouchStart = this._onTouchStart.bind(this);
        this._onTouchMove = this._onTouchMove.bind(this);
        this._touchStartX = 0;
        this._touchStartY = 0;

        this._menuToggle = null;
        this._header = null;
        this._tl = null;
        this._hamburgerTl = null;
        this._mm = null;
        this._isDesktop = false;
        this._initialized = false;

        // Dropdown config (easy to tweak)
        this._dropdownSpeed = 0.25;
        this._dropdownEase = 'power3.inOut';
        this._activeDropdownParent = null;
        this._dropdownCleanupFns = [];
    }

    // --- Dropdowns (data-dropdown-parent / data-dropdown-wrap) ---

    _getDropdownParents() {
        return Array.from(this.querySelectorAll('[data-dropdown-parent]'));
    }

    _getDropdownUnderlay() {
        const root = this.closest('header') || this.closest('.sticky');
        return root?.querySelector('[data-menu-underlay]') ?? null;
    }

    _isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this._menuToggle = document.getElementById('menu-toggle');
        this._header = this.closest('header');

        const line1 = this._menuToggle?.querySelector('[data-hamburger-line="1"]');
        const line2 = this._menuToggle?.querySelector('[data-hamburger-line="2"]');
        const line3 = this._menuToggle?.querySelector('[data-hamburger-line="3"]');

        this._mm = gsap.matchMedia();

        this._mm.add('(min-width: 1024px)', () => {
            this._isDesktop = true;
            gsap.set(this, { clearProps: 'clipPath' });
            const cleanup = this._setupDropdowns();
            return () => {
                if (typeof cleanup === 'function') cleanup();
            };
        });

        this._mm.add('(max-width: 1023px)', () => {
            this._isDesktop = false;
            gsap.set(this, { clipPath: 'inset(0% 0% 100% 0%)' });
            const cleanup = this._setupDropdowns();
            return () => {
                if (typeof cleanup === 'function') cleanup();
            };
        });

        this._tl = gsap.timeline({ paused: true });
        this._tl.to(this, {
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 0.6,
            ease: 'expo.inOut'
        });

        // Hamburger → Cross animation
        if (line1 && line2 && line3) {
            gsap.set([line1, line3], { transformOrigin: '50% 50%' });

            this._hamburgerTl = gsap.timeline({ paused: true });

            this._hamburgerTl

                .to(line1, {
                    y: 6,
                    duration: 0.25,
                    ease: 'power2.out'
                }, 0)
                .to(line3, {
                    y: -6,
                    duration: 0.25,
                    ease: 'power2.out'
                }, 0)


                .to(line2, {
                    autoAlpha: 0,
                    duration: 0.15
                }, 0.15)


                .to(line1, {
                    rotation: 45,
                    duration: 0.3,
                    ease: 'power2.out'
                }, 0.25)
                .to(line3, {
                    rotation: -45,
                    duration: 0.3,
                    ease: 'power2.out'
                }, 0.25);
        }

        if (this._menuToggle) {
            this._menuToggle.addEventListener('click', this._onMenuToggleClick);
        }

        // Keep scrolling inside the mobile menu instead of the page behind it
        this.style.overscrollBehavior = 'contain';
        this.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.addEventListener('touchmove', this._onTouchMove, { passive: false });
    }

    disconnectedCallback() {
        if (this._menuToggle) {
            this._menuToggle.removeEventListener('click', this._onMenuToggleClick);
        }
        this.removeEventListener('touchstart', this._onTouchStart);
        this.removeEventListener('touchmove', this._onTouchMove);

        this._mm?.revert();
        this._tl?.kill();
        this._hamburgerTl?.kill();
        this._dropdownCleanupFns.forEach((fn) => fn());
        this._dropdownCleanupFns = [];
        this._activeDropdownParent = null;

        this._initialized = false;
    }

    _dropdownShowUnderlay() {
        const underlay = this._getDropdownUnderlay();
        if (!underlay) return;
        underlay.classList.remove('hidden');
        gsap.killTweensOf(underlay);
        gsap.to(underlay, {
            opacity: 0.3,
            duration: this._dropdownSpeed,
            ease: this._dropdownEase
        });
    }

    _dropdownHideUnderlay() {
        const underlay = this._getDropdownUnderlay();
        if (!underlay) return;
        gsap.killTweensOf(underlay);
        gsap.to(underlay, {
            opacity: 0,
            duration: this._dropdownSpeed,
            ease: this._dropdownEase,
            onComplete: () => {
                if (!this._activeDropdownParent) underlay.classList.add('hidden');
            }
        });
    }

    _dropdownSetInactiveStates(activeParent) {
        if (!this._isDesktop) return;
        this._getDropdownParents().forEach((parent) => {
            const btn = parent.querySelector('[data-dropdown-button]');
            if (!btn) return;
            btn.classList.toggle('text-gray-400', parent !== activeParent);
        });
    }

    _dropdownResetAllStates() {
        this._getDropdownParents().forEach((parent) => {
            const btn = parent.querySelector('[data-dropdown-button]');
            if (btn) btn.classList.remove('text-gray-400');
        });
    }

    _setupDropdowns() {
        const underlay = this._getDropdownUnderlay();
        if (underlay) {
            gsap.set(underlay, { opacity: 0 });
            underlay.classList.add('hidden');
        }

        const parents = this._getDropdownParents();
        const speed = this._dropdownSpeed;
        const ease = this._dropdownEase;
        const isDesktop = this._isDesktop;
        const isTouch = this._isTouchDevice();

        parents.forEach((parent, index) => {
            const button = parent.querySelector('[data-dropdown-button]');
            const wrap = parent.querySelector('[data-dropdown-wrap]');
            const mobileWrap = parent.querySelector('[data-dropdown-mobile-wrap]');
            if (!button || !wrap) return;

            let isOpen = false;
            let tlDesktop = null;
            let tlMobile = null;
            let mobileWrapHeight = 0;

            wrap.hidden = true;
            button.setAttribute('aria-expanded', 'false');

            const setInitialState = () => {
                gsap.set(wrap, {
                    display: 'block',
                    opacity: 1,
                    ...(isDesktop && { clipPath: 'inset(0% 0% 100% 0%)' })
                });
                if (mobileWrap) gsap.set(mobileWrap, { clearProps: 'height,overflow' });
            };

            const createDesktopTimeline = () =>
                gsap
                    .timeline({
                        paused: true,
                        defaults: { duration: speed, ease },
                        onStart: () => {
                            wrap.hidden = false;
                            button.setAttribute('aria-expanded', 'true');
                        },
                        onReverseComplete: () => {
                            wrap.hidden = true;
                            button.setAttribute('aria-expanded', 'false');
                        }
                    })
                    .to(wrap, { opacity: 1, clipPath: 'inset(0% 0% 0% 0%)' });

            const createMobileTimeline = () => {
                const tl = gsap.timeline({
                    paused: true,
                    defaults: { duration: 0.6, ease: 'smooth' },
                    onStart: () => {
                        wrap.hidden = false;
                        button.setAttribute('aria-expanded', 'true');
                        gsap.set(wrap, { opacity: 0 });
                        if (mobileWrap) {
                            void wrap.offsetHeight;
                            mobileWrapHeight = mobileWrap.offsetHeight;
                            gsap.set(mobileWrap, { height: 0, overflow: 'hidden' });
                            if (mobileWrapHeight === 0) {
                                requestAnimationFrame(() => {
                                    requestAnimationFrame(() => {
                                        mobileWrapHeight = mobileWrap.offsetHeight;
                                        gsap.to(mobileWrap, { height: mobileWrapHeight, duration: 0.6, ease: 'smooth' });
                                    });
                                });
                            }
                        }
                    },
                    onReverseComplete: () => {
                        wrap.hidden = true;
                        button.setAttribute('aria-expanded', 'false');
                        if (mobileWrap) gsap.set(mobileWrap, { clearProps: 'height,overflow' });
                    }
                });
                if (mobileWrap) {
                    tl.to(mobileWrap, { height: () => mobileWrapHeight }, 0);
                }
                tl.to(wrap, { opacity: 1 }, 0);
                return tl;
            };

            const getActiveTimeline = () => (this._isDesktop ? tlDesktop : tlMobile);

            const plusLine = button.querySelector('[data-plus-line]');

            const setPlusLineMinus = () => {
                if (!plusLine || isDesktop) return;
                gsap.killTweensOf(plusLine);
                gsap.to(plusLine, { scaleX: 0, duration: speed, ease });
            };

            const setPlusLinePlus = () => {
                if (!plusLine || isDesktop) return;
                gsap.killTweensOf(plusLine);
                gsap.to(plusLine, {
                    scaleX: 1,
                    duration: speed,
                    ease,
                    onComplete: () => gsap.set(plusLine, { clearProps: 'transform' })
                });
            };

            const openDropdown = () => {
                if (isOpen) return;

                if (this._activeDropdownParent && this._activeDropdownParent !== parent) {
                    this._activeDropdownParent.__close(false);
                }
                this._activeDropdownParent = parent;

                if (isDesktop && !isTouch) {
                    this._dropdownSetInactiveStates(parent);
                    this._dropdownShowUnderlay();
                }
                if (!isDesktop) setPlusLineMinus();
                getActiveTimeline().play();
                isOpen = true;
            };

            const closeDropdown = (instant = false) => {
                if (!isOpen) return;

                if (!isDesktop) setPlusLinePlus();

                const tl = getActiveTimeline();
                if (instant) {
                    tl.progress(0).pause();
                    wrap.hidden = true;
                    button.setAttribute('aria-expanded', 'false');
                    if (mobileWrap) gsap.set(mobileWrap, { clearProps: 'height,overflow' });
                } else {
                    tl.reverse();
                }
                isOpen = false;

                if (this._activeDropdownParent === parent) {
                    this._activeDropdownParent = null;
                    if (isDesktop) {
                        this._dropdownResetAllStates();
                        this._dropdownHideUnderlay();
                    }
                }
            };

            const toggleDropdown = () => (isOpen ? closeDropdown() : openDropdown());

            const handleClick = (e) => {
                if (isDesktop && !isTouch) return;
                e.preventDefault();
                toggleDropdown();
            };

            const handleMouseEnter = () => {
                if (!isTouch && isDesktop) openDropdown();
            };

            const handleMouseLeave = () => {
                if (!isTouch && isDesktop) closeDropdown();
            };

            parent.__closeInstant = () => closeDropdown(true);
            parent.__close = (instant) => closeDropdown(instant);
        parent.__open = () => openDropdown();

            setInitialState();
            tlDesktop = createDesktopTimeline();
            tlMobile = createMobileTimeline();

            if (!isDesktop || isTouch) {
                button.addEventListener('click', handleClick);
                this._dropdownCleanupFns.push(() =>
                    button.removeEventListener('click', handleClick)
                );
            } else {
                parent.addEventListener('mouseenter', handleMouseEnter);
                parent.addEventListener('mouseleave', handleMouseLeave);
                this._dropdownCleanupFns.push(() => {
                    parent.removeEventListener('mouseenter', handleMouseEnter);
                    parent.removeEventListener('mouseleave', handleMouseLeave);
                });
            }

            this._dropdownCleanupFns.push(() => {
                tlDesktop?.kill();
                tlMobile?.kill();
                closeDropdown(true);
                if (plusLine) gsap.set(plusLine, { clearProps: 'transform' });
                if (mobileWrap) gsap.killTweensOf(mobileWrap);
                if (mobileWrap) gsap.set(mobileWrap, { clearProps: 'height,overflow' });
                delete parent.__closeInstant;
                delete parent.__close;
                delete parent.__open;
            });
        });

        return () => {
            this._dropdownCleanupFns.forEach((fn) => fn());
            this._dropdownCleanupFns = [];
            this._activeDropdownParent = null;
            this._dropdownResetAllStates();
            const u = this._getDropdownUnderlay();
            if (u) {
                gsap.killTweensOf(u);
                u.classList.add('hidden');
                gsap.set(u, { opacity: 0 });
            }
        };
    }

    _onTouchStart(e) {
        const touch = e.touches[0];
        this._touchStartX = touch.clientX;
        this._touchStartY = touch.clientY;
    }

    _onTouchMove(e) {
        if (this._isDesktop || !this.hasAttribute('data-menu-open')) return;

        // When the menu content fits (e.g. all dropdowns collapsed) the menu isn't
        // scrollable, so iOS hands vertical swipes to the page behind it.
        // Horizontal swipes are left alone for the product slider.
        const touch = e.touches[0];
        const dx = Math.abs(touch.clientX - this._touchStartX);
        const dy = Math.abs(touch.clientY - this._touchStartY);
        if (dy > dx && this.scrollHeight <= this.clientHeight && e.cancelable) {
            e.preventDefault();
        }
    }

    _onMenuToggleClick() {
        if (this._isDesktop) return;

        const isOpen = this.hasAttribute('data-menu-open');

        if (!isOpen) {
            const headerBottom = this._header.getBoundingClientRect().bottom;
            this.style.top = `${headerBottom}px`;

            document.documentElement.style.overflow = 'clip';
            document.body.style.overflow = 'clip';

            this.classList.remove('hidden');
            this.setAttribute('data-menu-open', '');

            if (!this._isDesktop) {
                const first = this._getDropdownParents()[0];
                if (first?.__open) first.__open();
            }
            this._tl.play();
            this._hamburgerTl?.play();
        } else {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            this.removeAttribute('data-menu-open');

            this._tl.reverse();
            this._hamburgerTl?.reverse();
        }
    }
}

customElements.define('header-menu', HeaderMenu);
